import React from 'react';
import {
  bodyArea,
  femaleBodyArea,
  BODY_AREA_KEY_MAP,
  PAIN_COLOR_MAP
} from '../constants/bodyAreaData';
import { ThreeMonthPlanItem } from '../../PersonalisedPlan/service/threeMonthPlanService';

interface BodyAreaDetailsSectionProps {
  gender: string;
  planItems: ThreeMonthPlanItem[];
}

// PDF-only appendix: every body area's full description + poses, shown as its own section
// (not tied to a specific figure/timeline) instead of behind a click. See downloadPersonalisedPlanPdf.ts.
const stripPoseSuffix = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '');

const healthColor = (level: string) => PAIN_COLOR_MAP[level] ?? '#A3A3A3';

const BodyAreaDetailsSection: React.FC<BodyAreaDetailsSectionProps> = ({ gender, planItems }) => {
  const baseBodyArea = gender.toLowerCase() === 'female' ? femaleBodyArea : bodyArea;
  const todayItem = planItems.find((p) => p.timeline === 'Today') ?? planItems[0];

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-sm flex flex-col gap-[35px] p-[25px] mt-[25px]">
      {baseBodyArea.map((area, areaIndex) => {
        const key = BODY_AREA_KEY_MAP[area.name];
        const currentHealth = String(todayItem?.[key] ?? '') || area.currentHealth;

        return (
          <div key={areaIndex} data-pdf-section className="flex flex-col gap-[12px]">
            <div className="flex flex-col gap-[4px]">
              <h2 className="text-[19px] leading-normal font-Inter font-semibold text-[#000] m-0">
                {area.name}
              </h2>
              <p className="text-[13px] leading-[22px] font-Inter font-[560] text-[#A3A3A3] m-0">
                Current Flexibility:{' '}
                <span className="font-[560]" style={{ color: healthColor(currentHealth) }}>
                  {currentHealth}
                </span>
              </p>
            </div>

            <div className="flex flex-col gap-[10px]">
              {area.descriptions.map((desc, descIndex) => (
                <p
                  key={descIndex}
                  className="text-[14px] leading-[22px] font-Inter font-normal text-[#757575] m-0"
                >
                  {desc}
                </p>
              ))}
              {area.poses.some((p) => p.description) && (
                <ul className="flex flex-col gap-[6px] list-none">
                  {area.poses
                    .filter((p) => p.description)
                    .map((pose, poseIndex) => (
                      <li
                        key={poseIndex}
                        className="text-[14px] leading-[22px] font-Inter font-normal text-[#757575] flex gap-[8px]"
                      >
                        <span className="mt-[2px] shrink-0">•</span>
                        <span>
                          <span className="font-medium text-[#757575]">{pose.name}</span> –{' '}
                          {pose.description}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-[10px] mt-[6px]">
              <div className="flex gap-[18px]">
                {area.poses.map((pose, poseIndex) => (
                  <p
                    key={poseIndex}
                    className="flex-1 text-[14px] leading-normal font-Inter font-medium text-[#000] m-0"
                  >
                    {stripPoseSuffix(pose.name)}
                  </p>
                ))}
              </div>
              <div className="flex gap-[18px]">
                {area.poses.map((pose, poseIndex) => (
                  <div
                    key={poseIndex}
                    className="flex-1 rounded-[13px] bg-[#e8e8e8] overflow-hidden flex items-center justify-center aspect-square"
                  >
                    {pose.image ? (
                      <img
                        src={pose.image}
                        alt={stripPoseSuffix(pose.name)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#e8e8e8]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BodyAreaDetailsSection;
